import { WalletBalanceHistoryDTO } from "@gymflow/types";
import { useInfiniteQuery } from "@tanstack/react-query";
import { useClubSettings } from "apps/portal/src/providers";
import { AuthenticationContext } from "apps/portal/src/providers/authentication/AuthenticationContext";
import { apiV2 } from "libs/api/src/lib/apiV2";
import { DateTime } from "luxon";
import { useCallback, useContext, useRef, useState } from "react";
import { DateRange } from "react-day-picker";
import { useTranslation } from "react-i18next";

import { Button, LoadingDots, Spinner } from "../../atoms";
import { EmptyPage } from "../../atoms/EmptyPage";
import { HostedPagesBackHeader } from "../components/HostedPagesBackHeader";
import HostedPagesOverviewCard from "../components/HostedPagesOverviewCard";
import WalletHistoryCard from "./components/WalletHistoryCard";
import WalletHistoryFilter from "./components/WalletHistoryFilter";

const transactionTypeMap: Record<string, string[]> = {
  topUp: ["CREDIT_TOP_UP", "CREDIT_REDEEM_VOUCHER", "CREDIT_PURCHASE_REFUND"],
  expense: ["DEBIT_PURCHASE", "DEBIT_TOP_UP_REFUND"],
};

const HostedPagesWalletHistory = () => {
  const listRef = useRef<HTMLDivElement>(null);
  const [filter, setFilter] = useState<{
    type: string[] | [];
    range?: DateRange;
  }>({
    type: [],
    range: undefined,
  });
  const { userId: userMemberId } = useContext(AuthenticationContext);
  const {
    club: { timezone },
  } = useClubSettings();
  const { t } = useTranslation();

  const dateFrom = filter?.range?.from
    ? DateTime.fromJSDate(filter.range.from)
        .setZone(timezone, { keepLocalTime: true })
        .toISODate()
    : null;

  const dateTo = filter?.range?.to
    ? DateTime.fromJSDate(filter.range.to)
        .setZone(timezone, { keepLocalTime: true })
        .toISODate()
    : null;

  const walletTransactionTypes = !filter?.type?.length
    ? undefined
    : filter.type.flatMap((t) => transactionTypeMap[t]);

  const {
    data: walletHistoryData,
    isFetching,
    fetchNextPage,
    hasNextPage,
    isLoading,
  } = useInfiniteQuery(
    apiV2.member.wallet.infiniteQueries.listHistory(userMemberId, {
      limit: 20,
      dateFrom,
      dateTo,
      walletTransactionTypes,
    }),
  );

  const handleScroll = useCallback(() => {
    const container = listRef.current;
    if (!container || isFetching || !hasNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight;

    if (distanceFromBottom < 100) {
      fetchNextPage();
    }
  }, [isFetching, hasNextPage, fetchNextPage]);

  const currentPageData = walletHistoryData?.pages.flatMap((e) => e.content);
  const isFilterVisible =
    currentPageData?.length || filter.range || filter.type.length;
  const isFilterApplied =
    !!filter.range || (filter.type && filter.type.length > 0);

  return (
    <div className="flex h-full flex-col">
      <HostedPagesBackHeader
        label={t("page.userWallets.title")}
        rightSideOptions={
          isFilterVisible ? (
            <WalletHistoryFilter onChange={setFilter} filter={filter} />
          ) : undefined
        }
      />
      <div
        ref={listRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto"
      >
        <div className="flex w-full flex-row justify-center">
          <HostedPagesOverviewCard.Container
            containerClassName="p-0 m-4"
            innerContainerStyle="p-0 border-0 w-full gap-2"
          >
            {currentPageData?.length === 0 && (
              <EmptyPage
                className="m-0 max-w-80 justify-center px-3 py-20"
                textOverride={
                  isFilterApplied
                    ? undefined
                    : t("page.userWallets.emptyPage.title")
                }
                description={
                  isFilterApplied
                    ? undefined
                    : t("page.userWallets.emptyPage.description")
                }
                action={
                  isFilterApplied ? (
                    <Button
                      onClick={() => setFilter({ range: undefined, type: [] })}
                    >
                      {t("page.userWallets.emptyPage.cta")}
                    </Button>
                  ) : undefined
                }
              />
            )}
            {currentPageData &&
              currentPageData.map((item: WalletBalanceHistoryDTO) => (
                <HostedPagesOverviewCard.InnerCard key={item.createdAt}>
                  <WalletHistoryCard {...item} />
                </HostedPagesOverviewCard.InnerCard>
              ))}
            {isLoading && <Spinner />}
            {currentPageData && isFetching && <LoadingDots />}
          </HostedPagesOverviewCard.Container>
        </div>
      </div>
    </div>
  );
};

export default HostedPagesWalletHistory;
